import { useNavigate } from 'react-router-dom';

import { VerticalContainer, useForm } from '@american-autonomy/ag-ui';

import Form from 'components/Form';
import FormSubmitPanel from 'components/FormSubmitPanel';
import BatteryFormFields from 'components/forms/BatteryFormFields';

import useBattery from 'hooks/useBattery';
import useFormDiscardConfirmation from 'hooks/useFormDiscardConfirmation';
import usePageRoutes from 'hooks/usePageRoutes';

import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { CreateBatteryFormSchema, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const CreateBatteryForm: FunctionComponent<Props> = ({
    className = '',
    onCancelClose,
    onClose,
    onCreated,
    requestingClose,
    testId = TEST_ID
}) => {
    const { createBattery, isLoading } = useBattery();
    const { CREATE } = useTranslation();
    const navigate = useNavigate();
    const { batteryDetailsPage } = usePageRoutes();

    const form = useForm<CreateBatteryFormSchema>({
        mode: 'onChange'
    });

    const {
        formState: { errors, isDirty },
        handleSubmit
    } = form;

    const { confirmationPanel, showConfirmation } = useFormDiscardConfirmation({
        isDirty,
        onCancelClose,
        onClose,
        requestingClose
    });

    const onSubmit = async (data: CreateBatteryFormSchema) => {
        const { name, serialNumber } = data;

        const battery = await createBattery({ battery: { name, serialNumber } });

        if (battery) {
            onCreated?.();
            navigate(batteryDetailsPage(battery.id));
        }
    };

    return (
        <VerticalContainer className={`${styles.createBatteryForm} ${className}`} testId={testId}>
            {showConfirmation ? (
                confirmationPanel
            ) : (
                <Form form={form}>
                    <BatteryFormFields />

                    <FormSubmitPanel
                        buttonText={CREATE}
                        disabled={isLoading}
                        errors={errors}
                        onSubmit={handleSubmit(onSubmit)}
                    />
                </Form>
            )}
        </VerticalContainer>
    );
};

export default CreateBatteryForm;
